/**
 * Resolve a github.com browse URL for an attention PR card.
 */

import { githubPullRequestHtmlUrl, parseGitHubPullRequestId } from '@zunelo/shared';
import type { AttentionItem, GitHubPullRequest } from './types';

function repoFromSecondary(secondary?: string): string | null {
  const repo = secondary?.split(' · ')[0]?.trim() ?? '';
  return repo.includes('/') ? repo : null;
}

function htmlUrlFromOwnerRepoNumber(
  owner: string | undefined,
  repo: string | undefined,
  number: number,
): string | null {
  if (!owner || !repo || !Number.isFinite(number) || number <= 0) return null;
  return githubPullRequestHtmlUrl(owner, repo, number);
}

/** Only allow https://github.com/.../pull/<n> URLs for external open. */
export function isSafeGitHubPrHtmlUrl(raw: string | undefined | null): boolean {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:') return false;
    if (url.hostname.toLowerCase() !== 'github.com') return false;
    return /\/pull\/\d+\/?$/.test(url.pathname);
  } catch {
    return false;
  }
}

function preferSafeExplicitUrl(...candidates: Array<string | undefined | null>): string | null {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed && isSafeGitHubPrHtmlUrl(trimmed)) return trimmed;
  }
  return null;
}

/** Prefer validated BFF `htmlUrl`, then stable id, then mock `owner/repo` + primary `#N`. */
export function resolveGitHubPrHtmlUrl(
  item: Pick<AttentionItem, 'id' | 'primary' | 'secondary' | 'htmlUrl' | 'rawPullRequest'>,
): string | null {
  const safeExplicit = preferSafeExplicitUrl(item.htmlUrl, item.rawPullRequest?.htmlUrl);
  if (safeExplicit) return safeExplicit;

  const fromPr = resolveFromPullRequest(item.rawPullRequest);
  if (fromPr) return fromPr;

  const parsed = parseGitHubPullRequestId(item.id);
  if (parsed?.owner && parsed.repo) {
    return htmlUrlFromOwnerRepoNumber(parsed.owner, parsed.repo, parsed.number);
  }

  const repo = repoFromSecondary(item.secondary);
  const number = Number.parseInt(item.primary.replace(/^#/, ''), 10);
  if (!repo || !Number.isFinite(number)) return null;
  const [owner, name] = repo.split('/');
  return htmlUrlFromOwnerRepoNumber(owner, name, number);
}

function resolveFromPullRequest(pr: GitHubPullRequest | undefined): string | null {
  if (!pr) return null;
  const parsed = parseGitHubPullRequestId(pr.id);
  if (parsed?.owner && parsed.repo) {
    return htmlUrlFromOwnerRepoNumber(parsed.owner, parsed.repo, parsed.number);
  }
  const repo = pr.head.repo?.trim() || pr.base.repo?.trim() || '';
  if (!repo.includes('/')) return null;
  const [owner, name] = repo.split('/');
  return htmlUrlFromOwnerRepoNumber(owner, name, pr.number);
}
